/**
 * File type utility functions for consistent file type handling and validation
 * Used to enforce file type consistency across conversation uploads
 */

export const FILE_TYPE_CATEGORIES = {
  PDF: ['application/pdf'],
  DOCUMENT: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  ],
  SPREADSHEET: [
    'text/csv',
    'application/vnd.ms-excel', // xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  ],
  IMAGE: [
    'image/jpeg',
    'image/jpg',
    'image/png'
  ]
} as const;

export type FileTypeCategory = keyof typeof FILE_TYPE_CATEGORIES;

/**
 * Get the file type category for a given MIME type
 * @param mimeType - The MIME type to categorize
 * @returns The file type category or null if unsupported
 */
export function getFileTypeCategory(mimeType: string): FileTypeCategory | null {
  for (const [category, types] of Object.entries(FILE_TYPE_CATEGORIES)) {
    if ((types as readonly string[]).includes(mimeType)) {
      return category as FileTypeCategory;
    }
  }
  return null;
}

/**
 * Get the file type category from a filename extension
 * @param filename - The filename to analyze
 * @returns The file type category or null if unsupported
 */
export function getFileCategoryFromFilename(filename: string): FileTypeCategory | null {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (!extension) return null;
  
  const extensionToMime: Record<string, string> = {
    'pdf': 'application/pdf',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'csv': 'text/csv',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'jpeg': 'image/jpeg',
    'jpg': 'image/jpeg',
    'png': 'image/png'
  };
  
  const mimeType = extensionToMime[extension];
  return mimeType ? getFileTypeCategory(mimeType) : null;
}

/**
 * Get a human-readable display name for a file type category
 * @param category - The file type category
 * @returns A display-friendly name
 */
export function getCategoryDisplayName(category: FileTypeCategory): string {
  switch (category) {
    case 'PDF':
      return 'PDF';
    case 'DOCUMENT':
      return 'Document';
    case 'SPREADSHEET':
      return 'Spreadsheet';
    case 'IMAGE':
      return 'Image';
    default:
      return 'Unknown';
  }
}

/**
 * Get all accepted MIME types as a flat array
 * @returns Array of all supported MIME types
 */
export function getAllAcceptedMimeTypes(): string[] {
  return Object.values(FILE_TYPE_CATEGORIES).flat();
}

/**
 * Check if a MIME type is supported
 * @param mimeType - The MIME type to check
 * @returns True if the MIME type is supported
 */
export function isSupportedMimeType(mimeType: string): boolean {
  return getAllAcceptedMimeTypes().includes(mimeType);
}